import { classifyMessageType, type ClassifiableMessage } from '@extension/wa-communication-layer/api/message-type-classifier';

/**
 * #658 — in-flight cap for MEDIA retries dispatched by MQTT/FCM wakes.
 *
 * The server scheduler wakes the engine once per due message and `insertOnlyIfMissing` used to start
 * a retry thunk for every wake at once. A campaign tick of ~100 group-image sends therefore ran ~100
 * concurrent uploads in one content script, the WhatsApp page stalled, acks landed minutes later and
 * the 180s guard wrote `failed` on sends that were delivering. Text and polls are cheap and stay ungated.
 */

/** Media = anything that uploads an asset (image, video, audio, document, other). */
export function isMediaSend(message: ClassifiableMessage | null | undefined): boolean {
  const type = classifyMessageType(message);
  return type !== 'text' && type !== 'poll';
}

export type GateOutcome = 'started' | 'queued' | 'duplicate';

export interface InFlightGate {
  /** Run `task` now if a slot is free, else queue it (FIFO). A key already in flight or queued is skipped. */
  submit(key: string, task: () => Promise<unknown>): GateOutcome;
  inFlight(): number;
  queued(): number;
}

export interface InFlightGateOptions {
  /** Max tasks running at once. */
  cap: number;
  /** A slot is force-released this long after its task started, so a hung task cannot wedge the queue. */
  maxHoldMs: number;
}

export function createInFlightGate({ cap, maxHoldMs }: InFlightGateOptions): InFlightGate {
  const active = new Set<string>();
  const waiting: Array<{ key: string; task: () => Promise<unknown> }> = [];

  const pump = (): void => {
    while (active.size < cap && waiting.length > 0) {
      const next = waiting.shift()!;
      run(next.key, next.task);
    }
  };

  const run = (key: string, task: () => Promise<unknown>): void => {
    active.add(key);
    let released = false;
    const release = (): void => {
      if (released) return;
      released = true;
      clearTimeout(timer);
      active.delete(key);
      pump();
    };
    const timer = setTimeout(release, maxHoldMs);
    try {
      void task().then(release, release);
    } catch {
      release();
    }
  };

  return {
    submit(key, task) {
      if (active.has(key) || waiting.some((w) => w.key === key)) return 'duplicate';
      if (active.size < cap) {
        run(key, task);
        return 'started';
      }
      waiting.push({ key, task });
      return 'queued';
    },
    inFlight: () => active.size,
    queued: () => waiting.length,
  };
}
